import { useEffect, useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

export type DocumentCopySourceType = 'quotation' | 'order' | 'invoice';

export type DocumentCopyLineMapping = {
  sourceLineId: string | null;
  sourceLineNumber: number;
  targetLineId: string;
  targetLineNumber: number;
};

export type DocumentCopyResult = {
  sourceType: DocumentCopySourceType;
  sourceId: string;
  sourceDocumentType: string;
  sourceDocumentNumber: string;
  copiedLineCount: number;
  copiedLineIds: string[];
  lineMappings: DocumentCopyLineMapping[];
};

type CopySourceListItem = {
  source_id: string;
  document_type: string;
  document_number: string;
  counterparty_name?: string | null;
  currency?: string | null;
  total_amount?: number;
  status?: string | null;
  updated_at?: string | null;
};

type CopyCommercialLine = {
  sourceLineId: string | null;
  lineNumber: number;
  description: string;
  lineType: string | null;
  skuCode: string | null;
  chargeCode: string | null;
  quantity: number;
  uom: string | null;
  unitPrice: number;
  amount: number;
  taxRate: number;
  taxAmount: number;
};

type CopySourceDocument = {
  sourceType: DocumentCopySourceType;
  sourceId: string;
  documentType: string;
  documentNumber: string;
  status: string | null;
  counterpartyName: string | null;
  currency: string | null;
  totalAmount: number;
  lines: CopyCommercialLine[];
};

export type DocumentCopySourceDocument = CopySourceDocument;

interface DocumentCopySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  targetId: string;
  targetLabel: string;
  endpoints: {
    listPath: string;
    detailPath: string;
    copyPath: string;
  };
  onCopied: (copyResult: DocumentCopyResult, sourceDetail: DocumentCopySourceDocument | null) => Promise<void> | void;
}

function fmtAmount(value: number, currency = 'SGD'): string {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);
}

export function DocumentCopySheet({
  open,
  onOpenChange,
  title,
  description,
  targetId,
  targetLabel,
  endpoints,
  onCopied,
}: DocumentCopySheetProps) {
  const [sourceType, setSourceType] = useState<DocumentCopySourceType>('quotation');
  const [search, setSearch] = useState('');
  const [sources, setSources] = useState<CopySourceListItem[]>([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
  const [sourceDetail, setSourceDetail] = useState<CopySourceDocument | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setSearch('');
      setSources([]);
      setSelectedSourceId(null);
      setSourceDetail(null);
      setSelectedLineIds([]);
      setError('');
      setSaving(false);
      setLoadingSources(false);
      setLoadingDetail(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingSources(true);
    setError('');
    api.get<{ sourceType: DocumentCopySourceType; items: CopySourceListItem[] }>(endpoints.listPath, {
      sourceType,
      search: search || undefined,
      limit: 30,
    })
      .then((result) => {
        if (cancelled) return;
        const nextItems = result.items ?? [];
        setSources(nextItems);
        setSelectedSourceId((current) => {
          if (current && nextItems.some((item) => item.source_id === current)) return current;
          return nextItems[0]?.source_id ?? null;
        });
      })
      .catch((nextError) => {
        if (cancelled) return;
        setSources([]);
        setSelectedSourceId(null);
        setError(nextError instanceof Error ? nextError.message : 'Failed to load copy history');
      })
      .finally(() => {
        if (!cancelled) setLoadingSources(false);
      });
    return () => {
      cancelled = true;
    };
  }, [endpoints.listPath, open, search, sourceType]);

  useEffect(() => {
    if (!open || !selectedSourceId) {
      setSourceDetail(null);
      setSelectedLineIds([]);
      return;
    }
    let cancelled = false;
    setLoadingDetail(true);
    setError('');
    api.get<{ sourceType: DocumentCopySourceType; source: CopySourceDocument }>(endpoints.detailPath, {
      sourceType,
      sourceId: selectedSourceId,
    })
      .then((result) => {
        if (cancelled) return;
        const detail = result.source;
        setSourceDetail(detail);
        setSelectedLineIds(detail.lines.map((line) => line.sourceLineId).filter((lineId): lineId is string => Boolean(lineId)));
      })
      .catch((nextError) => {
        if (cancelled) return;
        setSourceDetail(null);
        setSelectedLineIds([]);
        setError(nextError instanceof Error ? nextError.message : 'Failed to load source lines');
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [endpoints.detailPath, open, selectedSourceId, sourceType]);

  const selectableCount = useMemo(
    () => sourceDetail?.lines.filter((line) => line.sourceLineId).length ?? 0,
    [sourceDetail],
  );

  const allSelected = selectableCount > 0 && selectedLineIds.length === selectableCount;

  const toggleLine = (lineId: string, checked: boolean) => {
    setSelectedLineIds((current) => (
      checked ? Array.from(new Set([...current, lineId])) : current.filter((candidate) => candidate !== lineId)
    ));
  };

  const handleToggleAll = (checked: boolean) => {
    if (!sourceDetail) return;
    if (checked) {
      setSelectedLineIds(sourceDetail.lines.map((line) => line.sourceLineId).filter((lineId): lineId is string => Boolean(lineId)));
      return;
    }
    setSelectedLineIds([]);
  };

  const handleCopy = async () => {
    if (!selectedSourceId) {
      setError('Please select a source document');
      return;
    }
    if (selectedLineIds.length === 0) {
      setError('Please select at least one line');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await api.post<{ copyResult?: DocumentCopyResult }>(endpoints.copyPath, {
        mode: 'COPY_LINES',
        sourceType,
        sourceId: selectedSourceId,
        targetId,
        selectedLineIds,
      });
      const copyResult = result.copyResult ?? {
        sourceType,
        sourceId: selectedSourceId,
        sourceDocumentType: sourceDetail?.documentType ?? sourceType.toUpperCase(),
        sourceDocumentNumber: sourceDetail?.documentNumber ?? selectedSourceId,
        copiedLineCount: selectedLineIds.length,
        copiedLineIds: [],
        lineMappings: [],
      };
      await onCopied(copyResult, sourceDetail);
      onOpenChange(false);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : `Failed to copy lines into ${targetLabel}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col overflow-hidden sm:max-w-5xl">
        <SheetHeader className="shrink-0">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          <Tabs value={sourceType} onValueChange={(value) => setSourceType(value as DocumentCopySourceType)} className="space-y-3">
            <TabsList className="grid grid-cols-3">
              <TabsTrigger value="quotation">Quotation</TabsTrigger>
              <TabsTrigger value="order">Order</TabsTrigger>
              <TabsTrigger value="invoice">Invoice</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search history by number, counterparty, note"
              className="pl-9"
            />
          </div>

          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          ) : null}

          <div className="grid min-h-0 gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
            <div className="min-h-[420px] space-y-3 overflow-y-auto rounded-2xl border bg-muted/10 p-3">
              <div className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">History</div>
              {loadingSources ? (
                <div className="flex min-h-[180px] items-center justify-center text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading history...
                </div>
              ) : sources.length === 0 ? (
                <div className="rounded-xl border border-dashed bg-background px-3 py-6 text-center text-sm text-muted-foreground">
                  No {sourceType} history found.
                </div>
              ) : (
                sources.map((item) => {
                  const active = item.source_id === selectedSourceId;
                  return (
                    <div
                      key={item.source_id}
                      className={`w-full rounded-2xl border px-3 py-3 text-left transition ${
                        active ? 'border-sky-300 bg-sky-50 shadow-sm' : 'bg-background hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-medium text-slate-900">{item.document_number}</div>
                          <div className="text-xs text-slate-500">{item.document_type}</div>
                        </div>
                        {item.status ? <Badge variant="outline">{item.status}</Badge> : null}
                      </div>
                      <div className="mt-2 text-sm text-slate-600">{item.counterparty_name || 'No counterparty'}</div>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <div className="text-xs text-slate-500">
                          {fmtAmount(Number(item.total_amount ?? 0), item.currency || 'SGD')}
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant={active ? 'default' : 'outline'}
                          onClick={() => setSelectedSourceId(item.source_id)}
                        >
                          {active ? 'Selected' : 'Select'}
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="min-h-[420px] space-y-3 overflow-y-auto rounded-2xl border bg-background p-3">
              {loadingDetail ? (
                <div className="flex min-h-[240px] items-center justify-center text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading source detail...
                </div>
              ) : !sourceDetail ? (
                <div className="rounded-xl border border-dashed bg-muted/10 px-3 py-8 text-center text-sm text-muted-foreground">
                  Select a source document to choose lines.
                </div>
              ) : (
                <>
                  <Card className="border-sky-200 bg-sky-50/60">
                    <CardContent className="space-y-3 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-xs font-medium uppercase tracking-[0.18em] text-sky-700">Source Document</div>
                          <div className="mt-1 text-lg font-semibold text-slate-900">{sourceDetail.documentNumber}</div>
                          <div className="text-sm text-slate-600">{sourceDetail.counterpartyName || 'No counterparty'}</div>
                        </div>
                        {sourceDetail.status ? <Badge variant="outline">{sourceDetail.status}</Badge> : null}
                      </div>
                      <div className="grid gap-3 md:grid-cols-3">
                        <div className="rounded-xl border bg-white px-3 py-2">
                          <div className="text-xs text-muted-foreground">Document Type</div>
                          <div className="text-sm font-semibold">{sourceDetail.documentType}</div>
                        </div>
                        <div className="rounded-xl border bg-white px-3 py-2">
                          <div className="text-xs text-muted-foreground">Currency</div>
                          <div className="text-sm font-semibold">{sourceDetail.currency || 'SGD'}</div>
                        </div>
                        <div className="rounded-xl border bg-white px-3 py-2">
                          <div className="text-xs text-muted-foreground">Total</div>
                          <div className="text-sm font-semibold">{fmtAmount(sourceDetail.totalAmount, sourceDetail.currency || 'SGD')}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Copy Lines</div>
                      <div className="text-xs text-muted-foreground">
                        Select the lines to copy into this {targetLabel}. Each line is copied from history with current source mapping.
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <Checkbox checked={allSelected} onCheckedChange={(checked) => handleToggleAll(Boolean(checked))} />
                        Select all
                      </label>
                      <Badge variant="outline">{selectedLineIds.length} selected</Badge>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {sourceDetail.lines.map((line, index) => {
                      const lineId = line.sourceLineId ?? `virtual-${index}`;
                      const checked = line.sourceLineId ? selectedLineIds.includes(line.sourceLineId) : false;
                      return (
                        <div key={lineId} className="rounded-2xl border bg-muted/10 p-3">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={checked}
                              disabled={!line.sourceLineId}
                              onCheckedChange={(nextChecked) => {
                                if (!line.sourceLineId) return;
                                toggleLine(line.sourceLineId, Boolean(nextChecked));
                              }}
                            />
                            <div className="min-w-0 flex-1 space-y-2">
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                  <div className="font-medium text-slate-900">Line {line.lineNumber}</div>
                                  <div className="text-sm text-slate-700">{line.description}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {line.skuCode || line.chargeCode || line.lineType || 'General line'}
                                  </div>
                                </div>
                                <div className="text-right text-sm">
                                  <div className="font-semibold text-slate-900">{fmtAmount(line.amount, sourceDetail.currency || 'SGD')}</div>
                                  <div className="text-xs text-muted-foreground">Tax {fmtAmount(line.taxAmount, sourceDetail.currency || 'SGD')}</div>
                                </div>
                              </div>
                              <div className="grid gap-2 md:grid-cols-4">
                                <div className="rounded-xl border bg-white px-3 py-2">
                                  <div className="text-xs text-muted-foreground">Qty</div>
                                  <div className="text-sm font-semibold">{line.quantity}</div>
                                </div>
                                <div className="rounded-xl border bg-white px-3 py-2">
                                  <div className="text-xs text-muted-foreground">UOM</div>
                                  <div className="text-sm font-semibold">{line.uom || 'UNIT'}</div>
                                </div>
                                <div className="rounded-xl border bg-white px-3 py-2">
                                  <div className="text-xs text-muted-foreground">Unit Price</div>
                                  <div className="text-sm font-semibold">{fmtAmount(line.unitPrice, sourceDetail.currency || 'SGD')}</div>
                                </div>
                                <div className="rounded-xl border bg-white px-3 py-2">
                                  <div className="text-xs text-muted-foreground">Tax Rate</div>
                                  <div className="text-sm font-semibold">{line.taxRate}%</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <SheetFooter className="shrink-0 border-t bg-background">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={() => void handleCopy()} disabled={saving || loadingDetail || !sourceDetail || selectedLineIds.length === 0}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Copy Selected Lines
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
